import React, { useState } from 'react';
import useCenterlineAiStyles from './styles';
import { Feedback } from './core/chat-reducer';
import { FeedbackReason } from './core/types';
import { useCenterlineAi } from './CenterlineAiProvider';
import { ThumbDownGlyph, ThumbUpGlyph } from './Glyphs';

/**
 * Feedback under an answer (chat-ui-spec §8). Every change posts at once and fails silently: a
 * thumbs-down is sent before any reason, a reason chip re-sends it with the reason, and the note
 * is sent when the box loses focus.
 */

const REASONS: Array<[FeedbackReason, string]> = [
  ['wrong_number', 'Wrong number'],
  ['missing_docs', 'Missing docs'],
  ['too_slow', 'Too slow'],
  ['off_topic', 'Off topic'],
  ['other', 'Other'],
];

export default function FeedbackBar(props: {
  chatId: number;
  turnKey: string;
  feedback: Feedback;
}) {
  const { chatId, turnKey, feedback } = props;
  const classes = useCenterlineAiStyles();
  const { actions } = useCenterlineAi();
  const [note, setNote] = useState(feedback.note);

  return (
    <div className={classes.feedback}>
      <div className={classes.feedbackButtons}>
        <button
          type="button"
          className={classes.feedbackButton}
          aria-label="Good answer"
          aria-pressed={feedback.rating === 1}
          onClick={() => actions.rate(chatId, turnKey, 1)}
        >
          <ThumbUpGlyph />
        </button>
        <button
          type="button"
          className={classes.feedbackButton}
          aria-label="Bad answer"
          aria-pressed={feedback.rating === -1}
          onClick={() => actions.rate(chatId, turnKey, -1)}
        >
          <ThumbDownGlyph />
        </button>
      </div>
      {feedback.rating === -1 ? (
        <>
          <div className={classes.reasons} role="group" aria-label="What was wrong?">
            {REASONS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={classes.reason}
                aria-pressed={feedback.reason === value}
                onClick={() => actions.chooseReason(chatId, turnKey, value)}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            className={classes.note}
            placeholder="Anything else? (optional)"
            aria-label="Anything else? (optional)"
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => actions.saveNote(chatId, turnKey, note)}
          />
        </>
      ) : null}
    </div>
  );
}
